import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, MapPin, Home as HomeIcon, Pencil, Save, X } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const STATUS_LABEL = {
  borrador: 'Borrador', en_revision: 'En revisión', aprobado: 'Aprobado', publicado: 'Publicado',
};
const STATUS_STYLE = {
  borrador: 'bg-gray-100 text-gray-700',
  en_revision: 'bg-amber-100 text-amber-800',
  aprobado: 'bg-blue-100 text-blue-800',
  publicado: 'bg-emerald-100 text-emerald-800',
};

const fmtCOP = (n) => (n == null ? null : `$${Number(n).toLocaleString('es-CO')}`);

function ProfileEditor({ dev, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const start = () => {
    setForm({
      name: dev.name || '', phone: dev.phone || '', whatsapp: dev.whatsapp || '',
      email: dev.email || '', website: dev.website || '', logo_url: dev.logo_url || '',
      city: dev.city || '', description: dev.description || '',
    });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const updated = await api.updateDeveloperProfile(form);
      onSaved(updated); setOpen(false);
    } catch (e) { setErr(friendlyError(e)); } finally { setSaving(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!open) {
    return (
      <Button onClick={start} data-testid="dev-edit-profile-btn" variant="outline" className="rounded-full">
        <Pencil className="w-4 h-4 mr-2" /> Editar perfil
      </Button>
    );
  }
  return (
    <div className="w-full mt-4 rounded-2xl border border-gray-200 bg-white p-4" data-testid="dev-profile-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          ['name', 'Nombre de la empresa'], ['city', 'Ciudad'], ['phone', 'Teléfono'],
          ['whatsapp', 'WhatsApp'], ['email', 'Email'], ['website', 'Sitio web'], ['logo_url', 'URL del logo'],
        ].map(([k, label]) => (
          <label key={k} className="text-sm">
            <span className="text-xs uppercase tracking-widest text-gray-500">{label}</span>
            <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`dev-profile-${k}`}
              className="mt-1 w-full h-11 px-3 rounded-xl border border-gray-200 focus:border-habita outline-none" />
          </label>
        ))}
        <label className="text-sm sm:col-span-2">
          <span className="text-xs uppercase tracking-widest text-gray-500">Descripción</span>
          <textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3}
            className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-habita outline-none" />
        </label>
      </div>
      {err && <p className="text-sm text-rose-600 mt-2">{err}</p>}
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={saving} data-testid="dev-profile-save" className="rounded-full bg-habita hover:bg-habita-dark text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full"><X className="w-4 h-4 mr-2" /> Cancelar</Button>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={testid}>
      <p className="font-serif text-3xl font-black text-habita-dark">{value}</p>
      <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">{label}</p>
    </div>
  );
}

export default function DeveloperDashboard() {
  const [dev, setDev] = useState(null);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [d, s, p] = await Promise.all([api.developerMe(), api.developerSummary(), api.developerProjects()]);
      setDev(d); setSummary(s); setProjects(p.items || []);
    } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center"><p className="text-rose-600" data-testid="dev-dashboard-error">{error}</p></div>;
  }

  const c = summary?.counts || {};
  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="developer-dashboard">
      <SEO title="Mi constructora · Habita" description="Gestiona tu constructora y proyectos en Habita." />
      {/* Encabezado empresa */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {dev?.logo_url
            ? <img src={dev.logo_url} alt={dev.name} className="w-14 h-14 rounded-xl object-cover border border-gray-200" />
            : <div className="w-14 h-14 rounded-xl bg-habita-mint flex items-center justify-center"><Building2 className="w-7 h-7 text-habita" /></div>}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Mi constructora</p>
            <h1 className="font-serif text-2xl font-black text-habita-dark" data-testid="dev-company-name">{dev?.name}</h1>
          </div>
        </div>
        <ProfileEditor dev={dev} onSaved={setDev} />
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
        <StatCard label="Total" value={summary?.total ?? 0} testid="dev-stat-total" />
        <StatCard label="Borrador" value={c.borrador ?? 0} testid="dev-stat-borrador" />
        <StatCard label="En revisión" value={c.en_revision ?? 0} testid="dev-stat-en_revision" />
        <StatCard label="Aprobado" value={c.aprobado ?? 0} testid="dev-stat-aprobado" />
        <StatCard label="Publicado" value={c.publicado ?? 0} testid="dev-stat-publicado" />
      </div>

      {/* CTA + lista */}
      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="font-serif text-xl font-bold text-habita-dark">Mis proyectos</h2>
        <Link to="/mi-constructora/proyecto/nuevo" data-testid="dev-create-project-cta">
          <Button className="rounded-full bg-habita hover:bg-habita-dark text-white font-bold"><Plus className="w-4 h-4 mr-2" /> Crear nuevo proyecto</Button>
        </Link>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center" data-testid="dev-projects-empty">
          <Building2 className="w-10 h-10 text-gray-300 mx-auto" />
          <p className="text-gray-600 mt-3 font-medium">Aún no tienes proyectos.</p>
          <p className="text-gray-400 text-sm">Crea tu primer proyecto como borrador para empezar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dev-projects-grid">
          {projects.map((p) => (
            <Link key={p.id} to={`/mi-constructora/proyecto/${p.id}`} data-testid={`dev-project-card-${p.id}`}
              className="group rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-all">
              <div className="h-36 bg-gray-100">
                {p.cover_url && <img src={p.cover_url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-habita-dark truncate">{p.name}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_STYLE[p.review_status] || STATUS_STYLE.borrador}`}>
                    {STATUS_LABEL[p.review_status] || 'Borrador'}
                  </span>
                </div>
                {p.city && <p className="text-sm text-gray-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.city}</p>}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-700">
                  {p.price_min != null && <span className="font-semibold">Desde {fmtCOP(p.price_min)}</span>}
                  {p.units_count != null && <span className="flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> {p.units_count} unid.</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
